import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Page } from '../../components/page';
import {
  Feature,
  getFeatureByAddOn,
  getProductId,
  PlanInterval,
  SubscriptionSource,
  trueSelfServeAddOn,
} from '../../models';
import { useQuery } from '../../routes';
import { fromOrder, fromSubscription, useAppDispatch } from '../../store';
import { TrueSeatSelect } from './component/true-platform/TrueSeatSelect';
import { PriceSummary } from './component/true-platform/PriceSummary';
import { OrderSummary } from './component/true-platform/OrderSummary';
import { TestCloudSession } from './component/true-platform/TestCloudSession';
import { LoadingProgress } from '../../layout';
import { Panel } from '../../components/common/Panel';
import { usePreviewTrue } from '../../hook';
import { getCycleFromPathParam, getQuotaFromPathParam } from '../../utils/utils';
import OrderDetailSection from './component/OrderDetailSection';
import { BillingToggle } from './component/true-platform/BillingToggle';
import { PromoBanner } from './component/promo-banner/PromoBanner';

const useStyles = makeStyles(theme => ({
  headerStep: {
    color: '#ffffff',
    fontWeight: 800,
    fontSize: 16,
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderRadius: 12,
  },
  headerLabel: {
    fontWeight: 1000,
    fontSize: 16,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  platformLabel: {
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  cloudSessionsRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 2, 0, 2),
    gap: theme.spacing(1),
  },
  cloudIcon: {
    width: 22,
    height: 22,
    backgroundColor: '#5959EB',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },
  cloudSessionChip: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 600,
    fontSize: 12,
    height: 24,
    borderRadius: 4,
  },
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: '1px solid #e5e7eb',
  },
  mainCard: {
    border: '2px solid #5959EB',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 0 4px rgba(91,95,199,0.09), 0 4px 20px rgba(91,95,199,0.10)',
  },
  mainCardPadding: {
    padding: theme.spacing(2.5),
  },
  orderSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  starterPricingBadge: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 10,
    height: 22,
    borderRadius: 4,
  },
}));

export const PlanV2 = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const aod = get('aod') === 'true';
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const subscription = useSelector(
    fromSubscription.selectTrueSubscriptions(SubscriptionSource.RECURLY),
  );
  const cycle = getCycleFromPathParam(Feature.TRUE_TEAM);
  const [billingInterval, setBillingInterval] = useState<PlanInterval>(
    subscription?.billingInterval ?? PlanInterval.YEAR,
  );
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPendingOrder = !!pendingOrder && pendingOrder.length > 0;
  const isAnnual = billingInterval === PlanInterval.YEAR;
  const initAddOns: Record<string, number> = {};
  const disabledActionSx = hasPendingOrder
    ? { pointerEvents: 'none' as const, opacity: 0.5 }
    : undefined;

  // get init quota from params
  trueSelfServeAddOn.forEach(addOn => {
    const quota = getQuotaFromPathParam(getFeatureByAddOn(addOn)) ?? 0;
    if (quota > 0) {
      initAddOns[addOn.toUpperCase()] = quota;
    }
  });

  useEffect(() => {
    dispatch(fromOrder.setAddOns(initAddOns));
  }, []);

  const loading = useSelector(fromOrder.selectLoading);

  useEffect(() => {
    if (cycle && subscription?.billingInterval === PlanInterval.MONTH) {
      setBillingInterval(cycle);
    }
  }, [cycle]);

  const fetchData = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetOrderPreviewRequest()),
        dispatch(fromOrder.resetOrderPreviewResponse()),
      ]);
    }
  };

  usePreviewTrue();

  useEffect(() => {
    dispatch(fromOrder.doFetchAllOrder(accountId));
  }, [accountId, dispatch]);

  return (
    <Page
      id="plan-v2-page"
      key="plan-v2-page"
      headerId="page.plan.v2.header"
      fetchData={fetchData}
      topBanner={<PromoBanner accountId={accountId} />}
    >
      {pendingOrder && pendingOrder.length > 0
        && (
          <Box
            id="expand-plan-banner"
            display="flex"
            mt={2}
            border="1px solid #dfbc4a"
            borderRadius={2}
            maxWidth={1825}
          >
            <OrderDetailSection pendingOrder={pendingOrder[0]} />
          </Box>
        )}
      <Box
        id="plan-v2-content"
        key="plan-v2-content"
        display="flex"
        py={3}
        sx={theme => ({
          flexDirection: 'row',
          [theme.breakpoints.down('tabletL')]: {
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        })}
      >
        {/* Left panel */}
        <Panel
          id="plan-v2"
          step={1}
          headerLabel={(<FormattedMessage id="page.plan.v2.header" />)}
          footer={aod
            ? undefined
            : (
              <Box sx={disabledActionSx}>
                <TestCloudSession
                  key="test-cloud-session"
                  billingInterval={billingInterval}
                />
              </Box>
            )}
          sx={{
            width: '100%',
            minWidth: '70%',
            maxWidth: 1200,
            pr: 4,
            pb: 4,
            borderRight: '1px solid #BBBBBB',
          }}
        >
          <Box px={1}>
            {/* Plan title */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              mb={2}
            >
              <Typography fontWeight={600} lineHeight="135%" fontSize={32} color="#BF3DC2" mt={0.5}>
                <FormattedMessage id="page.plan.v2.plan.title" />
              </Typography>
              <BillingToggle
                key="billing-toggle"
                isAnnual={isAnnual}
                setBillingInterval={setBillingInterval}
                toggelDisabled={subscription !== undefined || hasPendingOrder}
              />
            </Box>

            <Box sx={disabledActionSx}>
              {/* Number of seats */}
              <TrueSeatSelect
                productId={getProductId(Feature.TRUE_TEAM)}
                billingInterval={billingInterval}
              />

              {/* Pricing row */}
              <PriceSummary isAnnual={isAnnual} />
            </Box>
          </Box>
        </Panel>

        {/* Right panel — Order Summary */}
        <Panel
          id="plan-v2-right"
          headerLabel={(
            <Box className={classes.orderSummaryHeader}>
              <FormattedMessage id="page.plan.v2.order.summary.title" />
            </Box>
          )}
          sx={theme => ({
            width: '30%',
            flexShrink: 0,
            [theme.breakpoints.down('tabletL')]: {
              width: '100%',
            },
          })}
          skipTransform
        >
          <OrderSummary isAnnual={isAnnual} />
        </Panel>
      </Box>
      {loading && (<LoadingProgress fullscreen />)}
    </Page>
  );
};
